import React from 'react';

import { getService } from '@Client/runner.hooks/useService';
import { SharedAngular } from '@Client/@types/sharedAngular';
import { ApiDateTime } from '@Shared.Angular/@types/dateTime';
import EmptyState from '@Client/runner.directives/common/EmptyState';
import FlowIconSquare from '@Client/runner.directives/common/FlowIconSquare/FlowIconSquare';
import IReviewDueOverview from '@Shared.Angular/@types/core/contracts/queryModel/governance/reviewDueOverview';
import PublishRequestEntityType from '@Shared.Angular/@types/core/contracts/constants/publishRequestEntityType';
import { ProcedureViewMode } from '@Shared.Angular/@types/core/contracts/constants/procedureViewMode';
import { STATE_MAPPINGS } from '@Shared.Angular/@types/stateMappings';

interface Props {
  appConfig: SharedAngular.APP_CONFIG;
  isMobile: boolean;
  filteredItems: IReviewDueOverview[];
}

const ProcessReviewDueList: React.FC<Props> = ({
  isMobile,
  filteredItems,
  appConfig
}) => {
  const flowinglyMomentService = getService('flowinglyMomentService');
  const tempModelerUrlService = getService('tempModelerUrlService');
  const $state = getService('$state');

  const getOverdueStatus = (processOverdueOnUtc: ApiDateTime) => {
    const processOverdueOnLocalDate = flowinglyMomentService.formatFullDate(
      processOverdueOnUtc,
      'YYYY-MM-DD'
    );
    const today = new Date();
    const todayLocalDate = `${today.getFullYear()}-${String(
      today.getMonth() + 1
    ).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const isOverdue = processOverdueOnLocalDate < todayLocalDate;
    return {
      statusText: isOverdue ? 'Overdue' : 'Due By',
      statusClass: isOverdue ? 'color-warning' : ''
    };
  };

  const getEntityTypeLabel = (item: IReviewDueOverview) =>
    item.entityType === PublishRequestEntityType.Procedure
      ? 'SOP'
      : item.entityTypeDescription || 'Process Map';

  const openReviewDueItem = (item: IReviewDueOverview) => {
    if (item.entityType === PublishRequestEntityType.Procedure) {
      $state.go(
        STATE_MAPPINGS.procedure,
        {
          procedureId: item.id,
          viewMode: ProcedureViewMode.ReadOnly
        },
        {}
      );
      return;
    }

    tempModelerUrlService.openModeler({ flowModelId: item.id });
  };

  return filteredItems.length > 0 ? (
    <div className={`flow-block ${isMobile ? 'isMobile' : ''}`}>
      <div className={`flow-list ${isMobile ? 'isMobile' : ''}`}>
        <ul>
          {filteredItems.map((reviewDueItem, index) => {
            const { statusText, statusClass } = getOverdueStatus(
              reviewDueItem.processOverdueOnUtc
            );

            return (
              <li key={`${reviewDueItem.id}-${index}`}>
                <div
                  className={`flow-start`}
                  id={`flowactive_${reviewDueItem.id}`}
                >
                  <div
                    className="flow-start-header flex flex-row"
                    onClick={() => openReviewDueItem(reviewDueItem)}
                  >
                    {!isMobile && (
                      <FlowIconSquare name={reviewDueItem.name} flowCode="" />
                    )}

                    {!isMobile && (
                      <div className="flow-list__contents flex flex-row">
                        <div className="flow-list__contents__subject">
                          <div className="flow-list__contents__subject__text flex-column">
                            {reviewDueItem.name}
                          </div>
                          <div className="flow-list__contents__subject__created-by flex-column">
                            {reviewDueItem.category}
                          </div>
                        </div>
                        <div className="flow-list__contents__subject">
                          <div className="flow-list__contents__subject__text flex-column">
                            {reviewDueItem.description
                              ? reviewDueItem.description
                              : '\u00A0'}
                          </div>
                          <div className="flow-list__contents__subject__created-by flex-column">
                            {getEntityTypeLabel(reviewDueItem)}
                          </div>
                        </div>
                        <div className="flow-list__contents__process-owner">
                          <div className="flow-list__contents__process-owner__text flex-column">
                            <div>Last Updated</div>
                            <div className="flow-list__contents__subject__created-by flex-column">
                              {flowinglyMomentService.formatUTCToLocal(
                                reviewDueItem.lastUpdatedUtc
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flow-list__contents__process-owner">
                          <div
                            className={`flow-list__contents__process-owner__text flex-column`}
                          >
                            <div className={`flow-status-text ${statusClass}`}>
                              {statusText}
                            </div>

                            <div className="flow-list__contents__subject__created-by flex-column">
                              {flowinglyMomentService.formatUTCToLocal(
                                reviewDueItem.processOverdueOnUtc
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {isMobile && (
                      <div className="flow-start-mobile">
                        <div className="flex flow-header__main">
                          <FlowIconSquare
                            name={reviewDueItem.name}
                            flowCode=""
                          />
                          <div className="flow-list__contents flex flex-row">
                            <div className="flow-list__contents__subject--mobile mr-20">
                              <div className="flow-list__contents__subject__text flex-column">
                                {reviewDueItem.name}
                              </div>
                              <div className="flow-list__contents__subject__started-by flex-column">
                                {reviewDueItem.category}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flow-header__extra flow-header__extra--start-flow">
                          {reviewDueItem.description}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  ) : (
    <div className={`governance-list-empty ${isMobile ? 'isMobile' : ''}`}>
      <EmptyState
        summaryText={`There are no reviews scheduled in the next ${appConfig.flowModelReviewWindowDays} days`}
        bodyText={`You’re all set! No workflows, process maps, or SOPs are waiting for review at the moment`}
      />
    </div>
  );
};

export default ProcessReviewDueList;
